// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\UserActivity.jsx

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/userActivity.css";

const useAppContext = () => useContext(Context);

const SESSION_STATUS_LABELS = {
  ACTIVE: "Activa",
  EXPIRED: "Caducada",
  CLOSED: "Cerrada",
};

const UserActivity = () => {
  const { store, actions } = useAppContext();

  const getUserActivity = useMemo(() => {
    if (typeof actions?.getUserActivity === "function") {
      return actions.getUserActivity;
    }

    if (typeof actions?.userActivityActions?.getUserActivity === "function") {
      return actions.userActivityActions.getUserActivity;
    }

    return null;
  }, [actions]);

  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    user_id: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const loadActivity = useCallback(
    async (nextFilters) => {
      if (typeof getUserActivity !== "function") {
        setErrorMessage(
          "La acción getUserActivity no está disponible en el store.",
        );
        return;
      }

      setLoading(true);
      setErrorMessage("");

      const activeFilters = nextFilters || {
        date_from: "",
        date_to: "",
        user_id: "",
      };

      const requestFilters = {
        date_from: activeFilters.date_from,
        date_to: activeFilters.date_to,
      };

      if (store.role === "admin") {
        requestFilters.user_id = activeFilters.user_id;
      }

      try {
        const result = await getUserActivity(requestFilters);

        if (!result?.success) {
          setErrorMessage(
            result?.error || "No se pudo cargar la actividad de usuarios.",
          );
        }
      } catch (error) {
        setErrorMessage(
          error?.message || "No se pudo cargar la actividad de usuarios.",
        );
      } finally {
        setLoading(false);
      }
    },
    [getUserActivity, store.role],
  );

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    loadActivity(filters);
  };

  const handleClearFilters = () => {
    const cleanFilters = {
      date_from: "",
      date_to: "",
      user_id: "",
    };

    setFilters(cleanFilters);
    loadActivity(cleanFilters);
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return new Intl.DateTimeFormat("es-ES", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(date);
  };

  const formatDuration = (value) => {
    if (value === null || value === undefined) {
      return "En curso";
    }

    const totalSeconds = Number(value);

    if (Number.isNaN(totalSeconds)) {
      return "-";
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours} h ${minutes} min`;
    }

    if (minutes > 0) {
      return `${minutes} min ${seconds} s`;
    }

    return `${seconds} s`;
  };

  const getUserLabel = (activityLog) => {
    if (activityLog.username) {
      return activityLog.username;
    }

    if (activityLog.user_id) {
      return `Usuario #${activityLog.user_id}`;
    }

    return "-";
  };

  const getSessionStatusLabel = (activityLog) =>
    SESSION_STATUS_LABELS[activityLog.session_status] || "Desconocido";

  const getLogoutLabel = (activityLog) => {
    if (activityLog.logout_at) {
      return formatDate(activityLog.logout_at);
    }

    if (activityLog.session_status === "ACTIVE") {
      return "Sesión activa";
    }

    if (activityLog.session_status === "EXPIRED") {
      return "Caducada";
    }

    return "-";
  };

  const logs = Array.isArray(store.userActivityLogs)
    ? store.userActivityLogs
    : [];

  return (
    <div className="container mt-5 user-activity-page">
      <div className="page-header">
        <h1 className="page-title">Actividad de usuarios</h1>
        <p className="page-subtitle">
          Registro de conexiones y cierres de sesión del sistema.
        </p>
      </div>

      <form className="user-activity-filters" onSubmit={handleSubmit}>
        <div className="user-activity-filter-group">
          <label htmlFor="date_from">Desde</label>
          <input
            id="date_from"
            name="date_from"
            type="date"
            value={filters.date_from}
            onChange={handleChange}
          />
        </div>

        <div className="user-activity-filter-group">
          <label htmlFor="date_to">Hasta</label>
          <input
            id="date_to"
            name="date_to"
            type="date"
            value={filters.date_to}
            onChange={handleChange}
          />
        </div>

        {store.role === "admin" && (
          <div className="user-activity-filter-group">
            <label htmlFor="user_id">Usuario ID</label>
            <input
              id="user_id"
              name="user_id"
              type="number"
              min="1"
              value={filters.user_id}
              onChange={handleChange}
              placeholder="Todos"
            />
          </div>
        )}

        <div className="user-activity-filter-actions">
          <button className="btn btn-primary" type="submit" disabled={loading}>
            Filtrar
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={handleClearFilters}
            disabled={loading}
          >
            Limpiar
          </button>
        </div>
      </form>

      {loading && (
        <div className="user-activity-state user-activity-loading">
          Cargando actividad de usuarios...
        </div>
      )}

      {!loading && errorMessage && (
        <div className="user-activity-state user-activity-error">
          {errorMessage}
        </div>
      )}

      {!loading && !errorMessage && logs.length === 0 && (
        <div className="user-activity-state user-activity-empty">
          No hay registros de actividad para los filtros seleccionados.
        </div>
      )}

      {!loading && !errorMessage && logs.length > 0 && (
        <div className="user-activity-table-wrap">
          <table className="table table-compact user-activity-table">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Inicio de sesión</th>
                <th>Estado</th>
                <th>Cierre de sesión</th>
                <th>Duración</th>
                <th>IP</th>
                <th>Agente de usuario</th>
              </tr>
            </thead>

            <tbody>
              {logs.map((activityLog) => (
                <tr key={activityLog.id}>
                  <td className="cell-strong">{getUserLabel(activityLog)}</td>
                  <td>{formatDate(activityLog.login_at)}</td>
                  <td>{getSessionStatusLabel(activityLog)}</td>
                  <td>{getLogoutLabel(activityLog)}</td>
                  <td>{formatDuration(activityLog.session_duration)}</td>
                  <td>{activityLog.ip_address || "-"}</td>
                  <td className="user-activity-agent">
                    {activityLog.user_agent || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UserActivity;
